// ===========================================
// SituationCompte — Situation de compte client (refonte UX CRM)
// ===========================================
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
    Card, Table, Spin, Button, Grid, Tag, Avatar, Space, Collapse, Dropdown, Typography, Empty,
} from 'antd';
import {
    FileSearchOutlined, UserOutlined, PhoneOutlined, IdcardOutlined,
    BankOutlined, FileProtectOutlined, DollarOutlined, CheckCircleOutlined,
    CloseCircleOutlined, ArrowLeftOutlined, DownloadOutlined, FilePdfOutlined,
    FileExcelOutlined, MailOutlined, EnvironmentOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { clientsService } from '../../common/services';
import { useExporterName } from '../../common/hooks';
import { addExporterFooter } from '../../common/utils/pdfExport';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import logoCap from '../../assets/logo/logo-cap-assurances.JPG';
import { formatPhone, formatMontant, formatDate as fmtDate } from '../../common/utils/format';

import MiniKpiCard from '../clients/components/MiniKpiCard';

import './situation.page.scss';

const { Text } = Typography;

const fmt = (v) => formatMontant(v);

const moyenPaiementConfig = {
    especes:      { label: 'Espèces',      color: 'green' },
    cheque:       { label: 'Chèque',       color: 'blue' },
    virement:     { label: 'Virement',     color: 'purple' },
    mobile_money: { label: 'Mobile Money', color: 'orange' },
};

const SituationCompte = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const exporterName = useExporterName();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.sm;

    useEffect(() => {
        const fetchSituation = async () => {
            try {
                const res = await clientsService.getSituation(id);
                setData(res?.data || res);
            } catch (error) {
                toast.error('Erreur lors du chargement de la situation');
            } finally {
                setLoading(false);
            }
        };
        fetchSituation();
    }, [id]);

    if (loading) {
        return (
            <div className="situation-loading">
                <Spin size="large" tip="Chargement…" />
            </div>
        );
    }
    if (!data) {
        return (
            <div className="situation-empty">
                <Empty description="Données introuvables" />
                <Button type="primary" onClick={() => navigate(-1)} style={{ marginTop: 16 }}>Retour</Button>
            </div>
        );
    }

    const client = data.client || {};
    const clientName = client.type === 'entreprise'
        ? (client.raisonSociale || client.nom || '')
        : `${client.nom || ''} ${client.prenom || ''}`.trim();
    const initials = clientName.split(' ').filter(Boolean).map(w => w[0]).join('').substring(0, 2).toUpperCase();
    const isEntreprise = client.type === 'entreprise';
    const soldeGlobal = data.soldeGlobal || 0;
    const hasSolde = soldeGlobal > 0;

    // ─── Colonnes Résumé par contrat ─────────────────────────────────
    const contratColumns = [
        {
            title: 'N° Police',
            dataIndex: ['contrat', 'numeroPolice'],
            key: 'police',
            render: (t) => <span className="situation-police">{t || '—'}</span>,
        },
        {
            title: 'Type',
            dataIndex: ['contrat', 'typeAssurance'],
            key: 'type',
            render: (t) => <Tag color="geekblue" className="situation-tag">{t?.nom || t || 'N/A'}</Tag>,
        },
        {
            title: 'Compagnie',
            dataIndex: ['contrat', 'compagnie'],
            key: 'compagnie',
            render: (c) => c?.nom || '—',
        },
        {
            title: 'Statut',
            dataIndex: ['contrat', 'statut'],
            key: 'statut',
            render: (s) => {
                const colors = { actif: 'green', expire: 'red', resilie: 'volcano', renouvele: 'blue', suspendu: 'orange' };
                return <Tag color={colors[s] || 'default'} className="situation-tag">{s || '—'}</Tag>;
            },
        },
        {
            title: 'Échéance',
            dataIndex: ['contrat', 'dateEcheance'],
            key: 'echeance',
            render: (d) => fmtDate(d, { fallback: '—' }),
        },
        {
            title: 'Prime TTC',
            dataIndex: ['contrat', 'primeTTC'],
            key: 'prime',
            align: 'right',
            render: (v) => <span className="situation-amount">{fmt(v)}</span>,
        },
        {
            title: 'Payé',
            dataIndex: 'totalVerse',
            key: 'paye',
            align: 'right',
            render: (v) => <span className="situation-amount is-positive">{fmt(v)}</span>,
        },
        {
            title: 'Reste',
            dataIndex: 'resteAPayer',
            key: 'reste',
            align: 'right',
            render: (v) => (
                <span className={`situation-amount ${v > 0 ? 'is-warning' : 'is-positive'}`}>
                    {fmt(v)}
                </span>
            ),
        },
    ];

    const reglementColumns = [
        {
            title: 'Date',
            dataIndex: 'dateReglement',
            key: 'date',
            width: 140,
            render: (d) => fmtDate(d, { fallback: '—' }),
        },
        {
            title: 'Montant',
            dataIndex: 'montant',
            key: 'montant',
            width: 160,
            align: 'right',
            render: (v) => <span className="situation-amount is-positive">{fmt(v)}</span>,
        },
        {
            title: 'Mode',
            dataIndex: 'moyenPaiement',
            key: 'mode',
            width: 160,
            render: (m) => {
                const conf = moyenPaiementConfig[m] || { label: m || '—', color: 'default' };
                return <Tag color={conf.color} className="situation-tag">{conf.label}</Tag>;
            },
        },
        {
            title: 'Référence',
            dataIndex: 'reference',
            key: 'ref',
            render: (t) => t ? <span className="situation-ref">{t}</span> : <span className="situation-muted">—</span>,
        },
    ];

    const getSituationRows = () => {
        const items = data.situationParContrat || [];
        return items.map(item => ({
            risque: item.contrat?.typeAssurance?.nom || item.contrat?.typeAssurance || '',
            dateEffet: item.contrat?.dateEffet ? new Date(item.contrat.dateEffet).toLocaleDateString('fr-FR') : '',
            dateEcheance: item.contrat?.dateEcheance ? new Date(item.contrat.dateEcheance).toLocaleDateString('fr-FR') : '',
            prime: item.contrat?.primeTTC || 0,
            paye: item.totalVerse || 0,
            reste: item.resteAPayer || 0,
        }));
    };

    // ─── Exports PDF / Excel (logique préservée) ─────────────────────
    const fmtPdf = (v) => (v || 0).toLocaleString('fr-FR').replace(/\s/g, ' ');

    const handleExportPDF = () => {
        const doc = new jsPDF({ orientation: 'landscape' });
        const today = new Date().toLocaleDateString('fr-FR');
        const year = new Date().getFullYear();
        const pageWidth = doc.internal.pageSize.width;
        const centerX = pageWidth / 2;

        doc.addImage(logoCap, 'JPEG', 14, 8, 40, 16);
        doc.setFontSize(10);
        doc.setTextColor(0);
        doc.text(today, pageWidth - 14, 15, { align: 'right' });

        doc.setFontSize(14);
        doc.setFont(undefined, 'bolditalic');
        doc.setTextColor(0);
        doc.text(`SITUATION DE COMPTE ${clientName.toUpperCase()} ${year}`, centerX, 32, { align: 'center' });
        doc.setFont(undefined, 'normal');

        const rows = getSituationRows();
        const headers = ['RISQUES', 'DATE EFFET / ÉCHÉANCE', 'PRIME À PAYER', 'PAYÉ', 'RESTE À PAYER'];
        const bodyRows = rows.map(r => [
            r.risque,
            `${r.dateEffet} au ${r.dateEcheance}`,
            fmtPdf(r.prime),
            fmtPdf(r.paye),
            fmtPdf(r.reste),
        ]);
        bodyRows.push([
            '',
            'TOTAL',
            fmtPdf(data.totalPrimesTTC),
            fmtPdf(data.totalVersements),
            fmtPdf(data.soldeGlobal),
        ]);

        autoTable(doc, {
            startY: 40,
            head: [headers],
            body: bodyRows,
            styles: { fontSize: 9, cellPadding: 4, lineColor: [0, 0, 0], lineWidth: 0.3 },
            headStyles: { fillColor: [26, 43, 95], textColor: 255, fontStyle: 'bold', halign: 'center' },
            columnStyles: {
                0: { cellWidth: 80 },
                2: { halign: 'right' },
                3: { halign: 'right' },
                4: { halign: 'right' },
            },
            didParseCell: (hookData) => {
                if (hookData.section === 'body' && hookData.row.index === bodyRows.length - 1) {
                    hookData.cell.styles.fontStyle = 'bold';
                    hookData.cell.styles.fillColor = [240, 240, 240];
                    if (hookData.column.index === 0) {
                        hookData.cell.styles.lineWidth = 0;
                        hookData.cell.styles.fillColor = [255, 255, 255];
                    }
                }
            },
            margin: { bottom: 35 },
        });

        const items = data.situationParContrat || [];
        const itemsWithReglements = items.filter(it => (it.reglements || []).length > 0);

        if (itemsWithReglements.length > 0) {
            let currentY = doc.lastAutoTable.finalY + 10;
            doc.setFontSize(11);
            doc.setFont(undefined, 'bold');
            doc.text('DÉTAIL DES RÈGLEMENTS PAR CONTRAT', 14, currentY);
            doc.setFont(undefined, 'normal');
            currentY += 4;

            itemsWithReglements.forEach(item => {
                const numero = item.contrat?.numeroPolice || '';
                const typeNom = item.contrat?.typeAssurance?.nom || item.contrat?.typeAssurance || '';
                doc.setFontSize(9);
                doc.setFont(undefined, 'bold');
                doc.text(`${numero}${typeNom ? ' - ' + typeNom : ''}`, 14, currentY + 4);
                doc.setFont(undefined, 'normal');
                currentY += 6;

                autoTable(doc, {
                    startY: currentY,
                    head: [['Date', 'Montant', 'Mode', 'Référence']],
                    body: (item.reglements || []).map(r => [
                        r.dateReglement ? new Date(r.dateReglement).toLocaleDateString('fr-FR') : '',
                        fmtPdf(r.montant),
                        moyenPaiementConfig[r.moyenPaiement]?.label || r.moyenPaiement || '—',
                        r.reference || '—',
                    ]),
                    styles: { fontSize: 8, cellPadding: 3, lineColor: [0, 0, 0], lineWidth: 0.2 },
                    headStyles: { fillColor: [26, 43, 95], textColor: 255, fontStyle: 'bold', halign: 'center' },
                    columnStyles: {
                        0: { cellWidth: 35 },
                        1: { cellWidth: 45, halign: 'right' },
                        2: { cellWidth: 40 },
                    },
                    margin: { left: 14, right: 14, bottom: 35 },
                });
                currentY = doc.lastAutoTable.finalY + 4;
            });
        }

        const lastY = doc.lastAutoTable.finalY + 12;
        doc.setFontSize(9);
        doc.setTextColor(0);
        doc.setFont(undefined, 'italic');
        doc.text(`Fait à Abidjan, le ${today}`, 14, lastY);
        doc.setFont(undefined, 'normal');

        const pageCount = doc.internal.getNumberOfPages();
        const pageHeight = doc.internal.pageSize.height;
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setDrawColor(0, 0, 0);
            doc.setLineWidth(1);
            doc.line(14, pageHeight - 28, pageWidth - 14, pageHeight - 28);
            doc.setFontSize(8);
            doc.setTextColor(0);
            doc.setFont(undefined, 'bold');
            doc.text('Cocody 2 Plateaux Vallon ; Rue des Jardins, 28 BP 56 Abidjan 28 / Tel: (+225) 07 10 01 60 54 / 07 88 47 17 79 / 27 22 41 93 50', pageWidth / 2, pageHeight - 20, { align: 'center' });
            doc.setFont(undefined, 'normal');
            doc.setFontSize(7);
            doc.text('RCCM: CI-ABJ-03-2024-B13-023771 | NCC: 2400889R | infos@capassurances.com', pageWidth / 2, pageHeight - 14, { align: 'center' });
        }

        addExporterFooter(doc, exporterName);
        doc.save(`situation_${clientName.replace(/\s/g, '_')}_${today.replace(/\//g, '-')}.pdf`);
        toast.success('PDF exporté avec succès');
    };

    const handleExportExcel = async () => {
        const today = new Date().toLocaleDateString('fr-FR');
        const year = new Date().getFullYear();
        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet('Situation');
        ws.columns = [{ width: 35 }, { width: 25 }, { width: 18 }, { width: 18 }, { width: 18 }];

        const logoRes = await fetch(logoCap);
        const logoBlob = await logoRes.blob();
        const logoBuffer = await logoBlob.arrayBuffer();
        const logoId = wb.addImage({ buffer: logoBuffer, extension: 'jpeg' });
        ws.addImage(logoId, { tl: { col: 0, row: 0 }, ext: { width: 160, height: 60 } });
        ws.addRow([]); ws.addRow([]); ws.addRow([]);

        const titleRow = ws.addRow([`SITUATION DE COMPTE ${clientName.toUpperCase()} ${year}`]);
        ws.mergeCells(4, 1, 4, 5);
        titleRow.getCell(1).font = { bold: true, size: 14, color: { argb: '1A2B5F' } };
        titleRow.getCell(1).alignment = { horizontal: 'center' };
        const dateRow = ws.addRow([`Exporté le ${today}${exporterName ? `   |   Exporté par : ${exporterName}` : ''}`]);
        ws.mergeCells(5, 1, 5, 5);
        dateRow.getCell(1).font = { italic: true, size: 10, color: { argb: '888888' } };
        dateRow.getCell(1).alignment = { horizontal: 'center' };
        ws.addRow([]);

        const headers = ['RISQUES', 'DATE EFFET / ÉCHÉANCE', 'PRIME À PAYER', 'PAYÉ', 'RESTE À PAYER'];
        const headerRow = ws.addRow(headers);
        headerRow.eachCell(cell => {
            cell.font = { bold: true, color: { argb: 'FFFFFF' }, size: 11 };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '1A2B5F' } };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
        });

        const rows = getSituationRows();
        rows.forEach(r => {
            const row = ws.addRow([r.risque, `${r.dateEffet} au ${r.dateEcheance}`, fmtPdf(r.prime), fmtPdf(r.paye), fmtPdf(r.reste)]);
            row.eachCell(cell => {
                cell.border = { top: { style: 'thin', color: { argb: 'CCCCCC' } }, bottom: { style: 'thin', color: { argb: 'CCCCCC' } }, left: { style: 'thin', color: { argb: 'CCCCCC' } }, right: { style: 'thin', color: { argb: 'CCCCCC' } } };
                cell.alignment = { vertical: 'middle' };
            });
        });

        const totalRow = ws.addRow(['', 'TOTAL', fmtPdf(data.totalPrimesTTC), fmtPdf(data.totalVersements), fmtPdf(data.soldeGlobal)]);
        totalRow.eachCell(cell => {
            cell.font = { bold: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F0F0F0' } };
            cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
        });

        const buffer = await wb.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `situation_${clientName.replace(/\s/g, '_')}_${today.replace(/\//g, '-')}.xlsx`);
        toast.success('Excel exporté avec succès');
    };

    const exportMenuItems = [
        { key: 'pdf',   label: 'Exporter en PDF',   icon: <FilePdfOutlined style={{ color: '#C8102E' }} />, onClick: handleExportPDF },
        { key: 'excel', label: 'Exporter en Excel', icon: <FileExcelOutlined style={{ color: '#16a34a' }} />, onClick: handleExportExcel },
    ];

    // ─── Collapse items ──────────────────────────────────────────────
    const collapseItems = (data.situationParContrat || []).map((item, idx) => ({
        key: idx.toString(),
        label: (
            <div className="situation-collapse-label">
                <Space size={12} wrap>
                    <span className="situation-police">{item.contrat?.numeroPolice}</span>
                    <Tag color="geekblue" className="situation-tag">{item.contrat?.typeAssurance?.nom || 'N/A'}</Tag>
                    <Tag color={item.contrat?.statut === 'actif' ? 'green' : item.contrat?.statut === 'expire' ? 'red' : 'orange'} className="situation-tag">
                        {item.contrat?.statut}
                    </Tag>
                </Space>
                <Space size={16} wrap className="situation-collapse-amounts">
                    <span><span className="situation-muted">Prime :</span> <strong>{fmt(item.contrat?.primeTTC)}</strong></span>
                    <span><span className="situation-muted">Payé :</span> <strong className="situation-amount is-positive">{fmt(item.totalVerse)}</strong></span>
                    <span><span className="situation-muted">Reste :</span> <strong className={`situation-amount ${item.resteAPayer > 0 ? 'is-warning' : 'is-positive'}`}>{fmt(item.resteAPayer)}</strong></span>
                </Space>
            </div>
        ),
        children: (
            <Table
                dataSource={item.reglements || []}
                columns={reglementColumns}
                rowKey={(r) => r._id || `${r.dateReglement}-${r.montant}`}
                size="small"
                scroll={{ x: true }}
                locale={{ emptyText: <Empty description="Aucun règlement pour ce contrat" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                pagination={false}
                className="situation-inner-table"
            />
        ),
    }));

    return (
        <div className="situation-page">
            <div className="situation-layout">
                {/* ═══════════════ SIDEBAR ═══════════════ */}
                <aside className="situation-sidebar">
                    <Card className="situation-sidebar-card">
                        <button
                            type="button"
                            className="sidebar-back-btn"
                            onClick={() => navigate(`/dashboard/client/${id}`)}
                        >
                            <ArrowLeftOutlined /> Retour à la fiche client
                        </button>

                        <div className="sidebar-identity">
                            <div className={`sidebar-client-avatar ${isEntreprise ? 'is-entreprise' : 'is-particulier'}`}>
                                {initials || <UserOutlined />}
                            </div>
                            <h2 className="sidebar-name">{clientName || '—'}</h2>
                            <div className="sidebar-meta">
                                <Tag color={isEntreprise ? 'orange' : 'blue'} className="sidebar-type-tag">
                                    {isEntreprise ? 'ENTREPRISE' : 'PARTICULIER'}
                                </Tag>
                                {client.code && (
                                    <Tag icon={<IdcardOutlined />} className="sidebar-code-tag">
                                        {client.code}
                                    </Tag>
                                )}
                            </div>
                        </div>

                        <div className="sidebar-divider" />

                        <div className="sidebar-coord">
                            {client.telephone && (
                                <div className="sidebar-coord-item">
                                    <div className="sidebar-coord-icon"><PhoneOutlined /></div>
                                    <div className="sidebar-coord-body">
                                        <div className="sidebar-coord-label">Téléphone</div>
                                        <a href={`tel:${client.telephone}`} className="sidebar-coord-value sidebar-coord-link">
                                            {formatPhone(client.telephone)}
                                        </a>
                                    </div>
                                </div>
                            )}
                            {client.email && (
                                <div className="sidebar-coord-item">
                                    <div className="sidebar-coord-icon"><MailOutlined /></div>
                                    <div className="sidebar-coord-body">
                                        <div className="sidebar-coord-label">Email</div>
                                        <a href={`mailto:${client.email}`} className="sidebar-coord-value sidebar-coord-link">
                                            {client.email}
                                        </a>
                                    </div>
                                </div>
                            )}
                            {(client.adresse || client.ville) && (
                                <div className="sidebar-coord-item">
                                    <div className="sidebar-coord-icon"><EnvironmentOutlined /></div>
                                    <div className="sidebar-coord-body">
                                        <div className="sidebar-coord-label">Adresse</div>
                                        <div className="sidebar-coord-value">
                                            {[client.adresse, client.ville].filter(Boolean).join(', ') || '—'}
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className="sidebar-coord-item">
                                <div className="sidebar-coord-icon"><BankOutlined /></div>
                                <div className="sidebar-coord-body">
                                    <div className="sidebar-coord-label">Agence</div>
                                    <div className="sidebar-coord-value">{client.agence?.nom || '—'}</div>
                                </div>
                            </div>
                        </div>

                        <div className="sidebar-divider" />

                        <div className="sidebar-actions">
                            <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight" trigger={['click']}>
                                <Button
                                    type="primary"
                                    icon={<DownloadOutlined />}
                                    block
                                    className="sidebar-btn-export"
                                >
                                    Exporter la situation
                                </Button>
                            </Dropdown>
                        </div>
                    </Card>
                </aside>

                {/* ═══════════════ MAIN ═══════════════ */}
                <div className="situation-main">
                    {/* Titre et KPIs */}
                    <section className="situation-kpis" aria-label="Indicateurs financiers">
                        <MiniKpiCard
                            icon={<FileProtectOutlined />}
                            label="Contrats"
                            value={data.nombreContrats || 0}
                            tone="blue"
                        />
                        <MiniKpiCard
                            icon={<DollarOutlined />}
                            label="Total primes TTC"
                            value={fmt(data.totalPrimesTTC || 0)}
                            tone="indigo"
                        />
                        <MiniKpiCard
                            icon={<CheckCircleOutlined />}
                            label="Total versements"
                            value={fmt(data.totalVersements || 0)}
                            tone="green"
                        />
                        <MiniKpiCard
                            icon={<CloseCircleOutlined />}
                            label="Reste à payer"
                            value={fmt(soldeGlobal)}
                            tone={hasSolde ? 'red' : 'green'}
                        />
                    </section>

                    {/* Résumé par contrat */}
                    <Card className="situation-card">
                        <div className="situation-card-header">
                            <h3 className="situation-card-title">
                                <FileSearchOutlined /> Résumé par contrat
                            </h3>
                            <Tag color="geekblue" className="situation-count-tag">
                                {data.nombreContrats || 0} contrat{(data.nombreContrats || 0) > 1 ? 's' : ''}
                            </Tag>
                        </div>

                        <Table
                            dataSource={data.situationParContrat || []}
                            columns={contratColumns}
                            rowKey={(r) => r.contrat?._id}
                            size="middle"
                            scroll={{ x: 'max-content' }}
                            locale={{
                                emptyText: <Empty description="Aucun contrat" image={Empty.PRESENTED_IMAGE_SIMPLE} />,
                            }}
                            pagination={false}
                            summary={() => {
                                if (!data.situationParContrat?.length) return null;
                                return (
                                    <Table.Summary fixed>
                                        <Table.Summary.Row className="situation-summary">
                                            <Table.Summary.Cell index={0} colSpan={5}>
                                                <strong>TOTAL</strong>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={5} align="right">
                                                <strong className="situation-amount">{fmt(data.totalPrimesTTC)}</strong>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={6} align="right">
                                                <strong className="situation-amount is-positive">{fmt(data.totalVersements)}</strong>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={7} align="right">
                                                <strong className={`situation-amount ${hasSolde ? 'is-warning' : 'is-positive'}`}>
                                                    {fmt(soldeGlobal)}
                                                </strong>
                                            </Table.Summary.Cell>
                                        </Table.Summary.Row>
                                    </Table.Summary>
                                );
                            }}
                        />
                    </Card>

                    {/* Détail règlements par contrat */}
                    {collapseItems.length > 0 && (
                        <Card className="situation-card">
                            <div className="situation-card-header">
                                <h3 className="situation-card-title">
                                    <DollarOutlined /> Détail des règlements par contrat
                                </h3>
                            </div>
                            <div className="situation-collapse-wrapper">
                                <Collapse
                                    items={collapseItems}
                                    defaultActiveKey={['0']}
                                    className="situation-collapse"
                                />
                            </div>
                        </Card>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SituationCompte;
